export default {
  elements: {
    toggle: null,
    cart: null,
    content: null,
    itemsHeading: null,
    itemsContainer: null,
    actionsContainer: null,
    loadingContainer: null,
    emptyCartMessage: null,
    close: null,
    liveRegion: null,
    subtotalElement: null,
    templates: {
      cartItem: null
    }
  },
  cartCount: 0,
  cartData: null,
  cachedNonce: null,
  noncePromise: null,
  lastFocusedElementBeforeOpen: null,
  focusableElements: [],
  COOKIE_NAME: 'cart_count',
  COOKIE_EXPIRY_DAYS: 7,
  
  init() {
    // Get DOM references from pre-rendered templates
    this.cacheElements();
    if (!this.elements.toggle || !this.elements.cart) return;
    
    // Initialize cart count from cookie if available
    this.cartCount = window.initialCartCount !== undefined ? 
                     window.initialCartCount : 
                     this.getCartCountFromCookie() || 0;
    this.updateCartCountDisplay();
    
    // Bind events
    this.bindEvents();
    
    // Get reference to the live region
    this.elements.liveRegion = document.getElementById('cart-live-region');
    
    // Defer non-critical operations
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.updateCartContent();
        // Only fetch count from API if no cookie exists
        if (!this.getCartCountFromCookie()) {
          this.updateCartCount();
        }
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        this.updateCartContent();
        if (!this.getCartCountFromCookie()) {
          this.updateCartCount();
        }
      }, 500);
    }
  },
  
  announce(message) {
    if (this.elements.liveRegion) {
      this.elements.liveRegion.textContent = message;
    }
  },
  
  cacheElements() {
    this.elements.toggle = document.querySelector('.site-masthead-controls-link[href="/cart/"]');
    this.elements.cart = document.getElementById('site-cart');
    
    if (this.elements.cart) {
      this.elements.content = document.getElementById('site-cart-content');
      this.elements.itemsHeading = document.getElementById('site-cart-items-heading');
      this.elements.itemsContainer = document.getElementById('site-cart-items');
      this.elements.actionsContainer = document.getElementById('site-cart-actions');
      this.elements.loadingContainer = document.getElementById('site-cart-loading');
      this.elements.emptyCartMessage = document.getElementById('site-cart-empty');
      this.elements.close = document.getElementById('site-cart-close');
      this.elements.subtotalElement = document.getElementById('site-cart-subtotal');
      
      // Cache template
      this.elements.templates.cartItem = document.getElementById('cart-item-template');
    }
  },
  
  openCart(e) {
    if (e) e.preventDefault();
    
    // First check if the cart is already open to avoid reopening and resetting focus
    if (this.elements.cart.classList.contains('active')) {
      return;
    }
    
    // Save the element that had focus before opening the cart
    // Ensure we're capturing a valid focusable element
    const activeElement = document.activeElement;
    if (activeElement && activeElement !== document.body) {
      this.lastFocusedElementBeforeOpen = activeElement;
    } else {
      // If no good active element, default to the toggle
      this.lastFocusedElementBeforeOpen = this.elements.toggle;
    }
    
    // Log the element we're saving for debugging purposes
    console.log('Saving focus element:', this.lastFocusedElementBeforeOpen);
    
    // Show the cart
    this.elements.cart.classList.add('active');
    document.body.classList.add('cart-is-active');
    
    // Update ARIA states
    this.elements.toggle.setAttribute('aria-expanded', 'true');
    this.elements.cart.setAttribute('aria-hidden', 'false');
    
    // Announce to screen readers
    this.announce('Shopping cart opened');
    
    // Set focus to the close button
    if (this.elements.close) {
      setTimeout(() => {
        this.elements.close.focus();
      }, 100); // Small delay to ensure the cart is fully visible
    }
    
    // Fetch cart content
    this.updateCartContent();
    
    // Find and cache all focusable elements within the cart
    this.updateFocusableElements();
  },
  
  closeCart(e) {
    if (e) e.preventDefault();
    
    // Hide the cart
    this.elements.cart.classList.remove('active');
    document.body.classList.remove('cart-is-active');
    
    // Update ARIA states
    this.elements.toggle.setAttribute('aria-expanded', 'false');
    this.elements.cart.setAttribute('aria-hidden', 'true');
    
    // Announce to screen readers
    this.announce('Shopping cart closed');
    
    // Store the last focused element for reference
    const elementToFocus = this.lastFocusedElementBeforeOpen;
    
    // Use setTimeout to ensure the focus happens after any other events
    setTimeout(() => {
      // Return focus to the element that had focus before opening the cart
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        try {
          elementToFocus.focus();
          // Extra check to ensure focus was actually applied
          if (document.activeElement !== elementToFocus) {
            console.warn('Focus could not be returned to the original element');
            // Fallback to the cart toggle if focus couldn't be returned
            if (this.elements.toggle) {
              this.elements.toggle.focus();
            }
          }
        } catch (error) {
          console.error('Error when trying to return focus:', error);
          // Fallback to the cart toggle
          if (this.elements.toggle) {
            this.elements.toggle.focus();
          }
        }
      } else if (this.elements.toggle) {
        // If we don't have a valid element to focus, use the toggle button
        this.elements.toggle.focus();
      }
    }, 0);
  },
  
  handleToggle(e) {
    e.preventDefault();
    
    if (this.elements.cart.classList.contains('active')) {
      this.closeCart();
    } else {
      this.openCart();
    }
  },
  
  updateFocusableElements() {
    this.focusableElements = Array.from(
      this.elements.cart.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => el.offsetParent !== null); // Ensure element is visible
  },
  
  handleKeyDown(e) {
    // Only process if cart is open
    if (!this.elements.cart.classList.contains('active')) return;
    
    // ESC key closes the cart
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeCart();
      return;
    }
    
    // Focus trap with TAB key
    if (e.key === 'Tab') {
      // Make sure we have the latest list of focusable elements
      this.updateFocusableElements();
      
      if (this.focusableElements.length === 0) return;
      
      const firstFocusable = this.focusableElements[0];
      const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
      
      // Shift+Tab on first element moves to last element
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } 
      // Tab on last element moves to first element
      else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  },
  
  bindEvents() {
    // Toggle cart visibility
    if (this.elements.toggle) {
      this.elements.toggle.addEventListener('click', e => this.handleToggle(e));
    }
    
    if (this.elements.close) {
      this.elements.close.addEventListener('click', e => this.closeCart(e));
    }
    
    // Handle the "Continue Shopping" button with direct binding instead of delegation
    const continueShoppingBtn = document.getElementById('site-cart-return');
    if (continueShoppingBtn) {
      continueShoppingBtn.addEventListener('click', e => {
        e.preventDefault();
        this.closeCart(e);
      });
    }
    
    // Also add event delegation as a fallback for any dynamically added buttons
    this.elements.content.addEventListener('click', e => {
      const target = e.target.closest('#site-cart-return');
      if (target) {
        e.preventDefault();
        this.closeCart(e);
      }
    });
    
    // Key events for the entire document
    document.addEventListener('keydown', e => this.handleKeyDown(e));
    
    // Close cart when clicking outside
    document.addEventListener('click', e => {
      if (
          this.elements.cart !== e.target 
          && !this.elements.cart.contains(e.target)
          && this.elements.toggle !== e.target 
          && !this.elements.toggle.contains(e.target)
          && this.elements.cart.classList.contains('active')
      ) {
        this.closeCart();
      }    
    });

    // Form submission - use event delegation
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="add-to-cart"]')) {
        e.preventDefault();
        this.addToCart(e.target);
      }
    });

    // Event delegation for cart content
    if (this.elements.content) {
      this.elements.content.addEventListener('click', e => {
        // Find the remove button or its parent
        const target = e.target.matches('.remove-item') ? 
                        e.target : 
                        e.target.closest('.remove-item');
        
        if (target) {
          e.preventDefault();
          const itemKey = target.dataset.key || target.parentNode.dataset.key;
          this.removeFromCart(itemKey);
        }
      });
    }

    // Variation change handler - using event delegation
    document.addEventListener('change', e => {
      if (e.target.matches('input[name="variation_id"]')) {
        this.handleVariationChange(e);
      }
    });
  },

  handleVariationChange(e) {
    const form = e.target.closest('form');
    if (!form) return;
    
    const baseProductId = this.getProductIds(form.action).productId;
    const variationValue = e.target.value;
    const variationId = e.target.getAttribute('data-variation-id');
    const variationPrice = e.target.getAttribute('data-variation-price');
    
    // Update price if element exists
    const productPriceElement = form.querySelector('.site-catalog-product-price, .product-price');
    if (productPriceElement) {
      productPriceElement.innerText = `$${variationPrice}`;
    }
    
    // Always include variation ID in URL regardless of whether it matches baseProductId
    let newUrl = `/cart/?add-to-cart=${baseProductId}`;
    if (variationId) {
      newUrl += `&variation_id=${variationId}&variation_value=${variationValue}`;
    }
    
    // Update form properties
    form.action = newUrl;
    form.dataset.productId = baseProductId;
    
    // Always set the variation ID when available - this is the key fix
    if (variationId) {
      form.dataset.variationId = variationId;
    }
    
    // Set variation value
    if (variationValue) {
      form.dataset.variationValue = variationValue;
    }
  },

  addToCart(form) {
    console.log(form);
    // Get productId from data attribute (preferred) or fallback to parsing from action
    const productId = form.dataset.productId || this.getProductIds(form.action).productId;
    // Get variationId from data attribute (preferred) or fallback to parsing from action
    const variationId = form.dataset.variationId || this.getProductIds(form.action).variationId;
    const quantity = form.querySelector('input[name="quantity"]')?.value || 1;
    
    // Show loading state on the form
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
      // Add loading indicator for accessibility
      submitBtn.setAttribute('aria-busy', 'true');
    }
    
    // Prepare variation data
    let variation = '';
    // First check data attribute (more reliable)
    if (form.dataset.variationValue) {
      variation = [{
        "attribute": "Size",
        "value": form.dataset.variationValue.trim()
      }];
    } 
    // Fallback to checking form elements
    else {
      const variationRadio = form.querySelector('input[name="variation_id"]:checked');
      if (variationRadio && variationRadio.value) {
        variation = [{
          "attribute": "Size",
          "value": variationRadio.value.trim()
        }];
      }
    }
    
    // Send the request
    return this.getNonce()
      .then(nonce => {
        const endpoint = location.host.includes('localhost') 
          ? `https://example.com/wp-json/wc/store/v1/cart/add-item` 
          : `/wp-json/wc/store/v1/cart/add-item`;
  
        const requestBody = {
          id: variationId || productId,
          quantity: parseInt(quantity, 10),
          ...(variation !== "" && { variation: variation })
        };
  
        console.log('Cart add request:', requestBody);
  
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Nonce': nonce,
          },
          body: JSON.stringify(requestBody),
          credentials: 'include'
        });
      })
      .then(response => response.json())
      .then(result => {
        if (result.error) {
          throw new Error(result.message || 'Problem adding item to cart');
        }
        
        this.updateCartCount(result.items_count);
        this.updateCartContent();
        this.showNotification('Item added to cart');
        this.announce('Item added to cart');
        this.openCart();
      })
      .catch(error => {
        console.error('Add to cart failed:', error);
        this.showNotification(`${error.message}`, true);
        this.announce(`Error: ${error.message}`);
      })
      .finally(() => {
        // Reset form state
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('loading');
          submitBtn.setAttribute('aria-busy', 'false');
        }
      });
  },

  removeFromCart(itemKey) {
    // Find and update button state
    const buttonEl = this.elements.content.querySelector(`[data-key="${itemKey}"]`);
    const removeBtn = buttonEl?.querySelector('.remove-item');
    const textEl = removeBtn?.querySelector('.remove-item-text');
    const loadingEl = removeBtn?.querySelector('.remove-item-loading');
    
    if (removeBtn) {
      removeBtn.disabled = true;
      removeBtn.setAttribute('aria-busy', 'true');
      if (textEl) textEl.hidden = true;
      if (loadingEl) loadingEl.hidden = false;
    }
    
    const endpoint = location.host.includes('localhost') 
      ? `https://example.com/wp-json/wc/store/v1/cart/remove-item` 
      : `/wp-json/wc/store/v1/cart/remove-item`;
    
    return this.getNonce()
      .then(nonce => fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Nonce': nonce,
        },
        body: JSON.stringify({
          key: itemKey
        }),
        credentials: 'include'
      }))
      .then(response => response.json())
      .then(result => {
        if (result.error) {
          throw new Error(result.message || 'Problem removing item from cart');
        }
        
        this.updateCartCount(result.items_count);
        this.updateCartContent();
        this.showNotification('Item removed from cart');
        this.announce('Item removed from cart');
      })
      .catch(error => {
        console.error('Remove from cart failed:', error);
        this.showNotification(`${error.message}`, true);
        this.announce(`Error: ${error.message}`);
        
        // Reset button state
        if (removeBtn) {
          removeBtn.disabled = false;
          removeBtn.setAttribute('aria-busy', 'false');
          if (textEl) textEl.hidden = false;
          if (loadingEl) loadingEl.hidden = true;
        }
      });
  },

  updateCartContent() {
    const endpoint = location.host.includes('localhost') 
      ? `https://example.com/wp-json/wc/store/v1/cart` 
      : `/wp-json/wc/store/v1/cart`;

    // Show loading state
    this.elements.loadingContainer.hidden = false;
    if (this.elements.itemsHeading) this.elements.itemsHeading.hidden = true;
    if (this.elements.itemsContainer) this.elements.itemsContainer.hidden = true;
    if (this.elements.actionsContainer) this.elements.actionsContainer.hidden = true;
    if (this.elements.emptyCartMessage) this.elements.emptyCartMessage.hidden = true;
    
    fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        // Cache cart data
        this.cartData = data;
        
        // Update cart count and cookie
        this.updateCartCount(data.items_count);
        
        // Hide loading indicator
        this.elements.loadingContainer.hidden = true;
        
        if (data.items.length === 0) {
          this.showEmptyCart();
          this.announce('Your cart is empty');
        } else {
          this.renderCart(data);
          this.announce(`Cart updated. ${data.items.length} ${data.items.length === 1 ? 'item' : 'items'} in your cart.`);
        }
        
        // Update focusable elements after content change
        this.updateFocusableElements();
      })
      .catch(error => {
        console.error('Failed to get cart content:', error);
        this.elements.loadingContainer.textContent = 'Error loading cart content. Please try again.';
        this.elements.loadingContainer.setAttribute('role', 'alert');
        this.announce('Error loading cart content');
      });
  },

  showEmptyCart() {
    // Hide cart content elements
    this.elements.itemsContainer.hidden = true;
    this.elements.itemsHeading.hidden = true;
    
    // Show empty cart message
    this.elements.emptyCartMessage.hidden = false;
    
    // Show actions container
    this.elements.actionsContainer.hidden = false;
    
    // Hide loading indicator
    this.elements.loadingContainer.hidden = true;
  },

  formatPrice(price) {
    return `$${(price / 100).toFixed(2)}`;
  },

  renderCart(data) {
    // Show headings and container
    this.elements.itemsHeading.hidden = false;
    this.elements.itemsContainer.hidden = false;
    this.elements.actionsContainer.hidden = false;
    
    // Hide empty cart message
    if (this.elements.emptyCartMessage) {
      this.elements.emptyCartMessage.hidden = true;
    }
    
    // Clear previous items
    this.elements.itemsContainer.innerHTML = '';
    
    // Use document fragment for better performance
    const fragment = document.createDocumentFragment();
    const itemTemplate = this.elements.templates.cartItem;
    
    // Add all cart items using the template
    data.items.forEach((item, index) => {
      // Clone the template
      const itemNode = itemTemplate.content.cloneNode(true);
      const itemElement = itemNode.querySelector('.site-cart-item');
      
      // Add accessibility attributes
      itemElement.setAttribute('role', 'listitem');
      
      // Populate data
      itemElement.dataset.key = item.key;
      
      const img = itemNode.querySelector('img');
      if (img && item.images && item.images[0]) {
        const src = `/assets/img/${item.images[0].src.split('/').pop().split('.')[0]}-thumbnail.webp`;
        img.src = src;
        img.alt = item.name;
      }
      
      const nameElement = itemNode.querySelector('.h6');
      nameElement.textContent = item.name;
      nameElement.id = `cart-item-${index}-name`;
      
      const variationElem = itemNode.querySelector('.site-cart-item-variation');
      if (variationElem) {
        if (item.variation && item.variation[0] && item.variation[0].value) {
          variationElem.textContent = `Size: ${item.variation[0].value}`;
        } else {
          variationElem.style.display = 'none';
        }
      }
      
      const priceElement = itemNode.querySelector('.site-cart-item-price');
      priceElement.textContent = `${this.formatPrice(item.prices.price)} × ${item.quantity}`;
      
      const removeBtn = itemNode.querySelector('.remove-item');
      if (removeBtn) {
        removeBtn.dataset.key = item.key;
        removeBtn.setAttribute('aria-label', `Remove ${item.name} from cart`);
        removeBtn.setAttribute('aria-describedby', `cart-item-${index}-name`);
      }
      
      fragment.appendChild(itemNode);
    });
    
    // Append items to container
    this.elements.itemsContainer.appendChild(fragment);
    
    // Update total in the footer
    if (this.elements.subtotalElement && data.totals) {
      const subtotalAmount = this.elements.subtotalElement.querySelector('.site-cart-subtotal-amount');
      if (subtotalAmount) {
        subtotalAmount.textContent = this.formatPrice(data.totals.total_price);
      }
    }
  },

  getProductIds(url) {
    const addToCartMatch = url.match(/add-to-cart=(\d+)/);
    const variationMatch = url.match(/variation_id=(\d+)/);
    
    return {
      productId: addToCartMatch ? addToCartMatch[1] : null,
      variationId: variationMatch ? variationMatch[1] : null
    };
  },

  getNonce() {
    // Cache the nonce promise to avoid multiple requests
    if (this.cachedNonce) {
      return Promise.resolve(this.cachedNonce);
    }
    
    if (this.noncePromise) {
      return this.noncePromise;
    }
    
    const endpoint = location.host.includes('localhost') 
      ? `https://example.com/wp-json/wc/store/v1/cart/items` 
      : `/wp-json/wc/store/v1/cart/items`;
    
    this.noncePromise = fetch(endpoint)
      .then(response => {
        const nonce = response.headers.get('nonce');
        if (nonce) {
          this.cachedNonce = nonce;
        }
        return nonce;
      })
      .catch(error => {
        console.error('Failed to get nonce:', error);
        this.noncePromise = null;
        throw error;
      });
    
    return this.noncePromise;
  },

  updateCartCount(count) {
    if (count !== undefined) {
      this.cartCount = count;
      this.setCartCountCookie(count);
      this.updateCartCountDisplay();
      return Promise.resolve(count);
    }
    
    const endpoint = location.host.includes('localhost') 
      ? `https://example.com/wp-json/wc/store/v1/cart` 
      : `/wp-json/wc/store/v1/cart`;
    
    return fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        this.cartCount = data.items_count;
        this.setCartCountCookie(data.items_count);
        this.updateCartCountDisplay();
        return this.cartCount;
      })
      .catch(error => {
        console.error('Failed to get cart count:', error);
      });
  },
  
  updateCartCountDisplay() {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = this.cartCount;
      
      // Add visual-hidden spans for screen readers if they don't already exist
      const toggle = el.closest('.site-masthead-controls-link');
      if (toggle && !toggle.querySelector('.sr-only')) {
        const srText = document.createElement('span');
        srText.className = 'sr-only';
        srText.textContent = ' items in your cart';
        el.insertAdjacentElement('afterend', srText);
      }
    });
  },

  showNotification(message, isError = false) {
    // Create a new notification element
    const notification = document.createElement('div');
    notification.className = `notification ${isError ? 'error' : 'success'}`;
    notification.setAttribute('role', 'alert');
    notification.textContent = message;
    
    // Append to body
    document.body.appendChild(notification);
    
    // Set a timeout to remove the notification
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => notification.remove(), 300);
    }, 2700);
  },
  
  // Cookie management methods
  setCartCountCookie(count) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.COOKIE_EXPIRY_DAYS);
    
    const cookieValue = encodeURIComponent(count) + '; expires=' + expiryDate.toUTCString() + '; path=/; SameSite=Lax';
    document.cookie = this.COOKIE_NAME + '=' + cookieValue;
  },
  
  getCartCountFromCookie() {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(this.COOKIE_NAME + '=')) {
        const cookieValue = cookie.substring(this.COOKIE_NAME.length + 1);
        const decodedValue = decodeURIComponent(cookieValue.split(';')[0]);
        return parseInt(decodedValue) || 0;
      }
    }
    return null;
  },
  
  clearCartCountCookie() {
    document.cookie = this.COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
  }
}